import { api } from './client'
import type {
  FootballStatusResponse,
  ReplicaClockState,
  ReplicaJumpResult,
  ResourceSyncStatus,
  ResyncScope,
  SeasonState,
} from '../footballStatus'

// Admin-only dev tools that drive the whole app's internal season simulation server-side —
// every call applies to ALL users (real accounts + the 10 bots), not just the admin's own
// state. The server re-checks admin on each endpoint (403 otherwise); these
// wrappers only ever fire from the admin-gated dev rail.

/** Lock + auto-fill every user's predictions for the gameweek (the whole-app "deadline"). */
export function simulateDeadline(gameweek: number): Promise<{ ok: true }> {
  return api.post('/dev/simulate-deadline', { gameweek })
}

/** Flip the shared "results are in" flag for the gameweek (distinct from finalize) so every
 * user's live points surface against the actual results. */
export function revealResults(gameweek: number, revealed: boolean): Promise<{ ok: true }> {
  return api.post('/dev/reveal-results', { gameweek, revealed })
}

/** Score every user for the gameweek and advance the shared current gameweek. */
export function finalizeGameweek(gameweek: number): Promise<{ ok: true }> {
  return api.post('/dev/finalize-gameweek', { gameweek })
}

/** Loop deadline + finalize for all users through the (optional) target gameweek. */
export function simulateSeason(throughGameweek?: number): Promise<{ ok: true }> {
  return api.post('/dev/simulate-season', throughGameweek != null ? { throughGameweek } : {})
}

/** Apply the "play Live" tick's shared state change (deadline + finalize) for all users. The
 * goal-by-goal animation itself stays admin-local on the client. */
export function simulateGameweekLive(gameweek: number): Promise<{ ok: true }> {
  return api.post('/dev/simulate-gameweek-live', { gameweek })
}

/**
 * Wipe gameplay state + points for every user and bot (roster/memberships survive).
 *
 * The one dev tool that stays reachable in live mode — it is the only way out
 * of a bad state — so there it requires a typed `RESET` confirmation. Sending it in mock/replica
 * mode is harmless; the server only checks it when live.
 */
export function resetAllData(confirm?: string): Promise<{ ok: true; clockReset: boolean }> {
  return api.request<{ ok: true; clockReset: boolean }>('/dev/reset-all', {
    method: 'DELETE',
    body: JSON.stringify({ confirm: confirm ?? '' }),
  })
}

/**
 * Driver, base URL, live window, budget, quota, pauses, per-resource sync state and the current
 * gameweek's derived phase.
 *
 * Read-only and available in every mode — including `live`, which is the mode it exists for.
 * It reads our own tables and spends no provider requests, which is what makes a 10s
 * poll while the panel is open reasonable.
 */
export function getFootballStatus(): Promise<FootballStatusResponse> {
  return api.get<FootballStatusResponse>('/dev/football-status')
}

/**
 * Re-fetch football data. **Costs provider requests** — the only dev tool that
 * does — so the caller shows the estimate before firing it, and live mode needs a typed `RESYNC`.
 */
export interface ResyncResult {
  scope: ResyncScope
  deleted: Record<string, number>
  clearedSyncState: string[]
  /** Gameweeks whose scoring bookkeeping was cleared, so the next pass re-derives. */
  clearedScoring: number[]
  schedulerRunning: boolean
  /** Per resource: re-fetched, or why not. Replaces the old meaningless `syncRan`. */
  synced: ResourceSyncStatus[]
}

export function resyncFootballData(scope: ResyncScope, confirm?: string): Promise<ResyncResult> {
  return api.post('/dev/resync-football', { scope, confirm: confirm ?? '' })
}

/**
 * The factory reset: gameplay, football data, the replica's clock **and** the
 * season's dates. Costs provider requests, since it re-syncs what it wiped; live needs a typed
 * `RESET EVERYTHING`.
 */
export function resetEverything(
  confirm?: string,
  options: { teams?: boolean } = {}
): Promise<ResetEverythingResult> {
  return api.post('/dev/reset-everything', { confirm: confirm ?? '', teams: options.teams === true })
}

export interface ResetEverythingResult {
  deleted: Record<string, number>
  clearedSyncState: string[]
  clearedScoring: number[]
  teams: boolean
  replica: { ok: boolean; reanchored: boolean; seasonStartMs: number | null; reason: string | null }
  synced: ResourceSyncStatus[]
  schedulerPaused: boolean
}

// ── The replica's clock ───────────────────────────────────────────────────────────────────────
// Always through our own server: the browser never learns the football base URL or port 3002
// (the guard rail). Every one of these is refused with a 409 outside replica mode.

export function getReplicaClock(): Promise<ReplicaClockState> {
  return api.get<ReplicaClockState>('/dev/replica/clock')
}

export function setReplicaClock(change: {
  nowMs?: number
  advanceMinutes?: number
  speed?: number
}): Promise<ReplicaClockState> {
  return api.post<ReplicaClockState>('/dev/replica/clock', change)
}

/** Back to the configured season start, speed 0. */
export function resetReplicaClock(): Promise<ReplicaClockState> {
  return api.post<ReplicaClockState>('/dev/replica/reset')
}

/** Jump to a derived instant — next deadline, next kickoff, full time — rather than an epoch. */
export function jumpReplicaClock(
  to: 'next-deadline' | 'next-kickoff' | 'next-fulltime' | 'gameweek-deadline',
  gameweek?: number
): Promise<ReplicaJumpResult> {
  return api.post<ReplicaJumpResult>('/dev/replica/jump', { to, gameweek })
}

/** Fault injection: void a fixture, or make an endpoint fail (the backoff). */
export function postponeReplicaFixture(
  fixture: string,
  restore?: boolean
): Promise<{ postponed: string[] }> {
  return api.post('/dev/replica/postpone', { fixture, restore })
}

/** One press per state worth testing. Replica-only, like the clock controls. */
export const SCENARIO_PRESETS = [
  'fresh-season',
  'before-deadline',
  'at-deadline',
  'matchday-in-play',
  'full-time',
  'settled',
  'mid-season',
] as const

export type ScenarioPreset = (typeof SCENARIO_PRESETS)[number]

export interface ScenarioResult {
  scenario: ScenarioPreset
  gameweek: number | null
  label: string
  steps: string[]
  clock: ReplicaClockState
  state: SeasonState
}

export function runScenario(
  preset: Exclude<ScenarioPreset, 'fresh-season'>,
  gameweek?: number
): Promise<ScenarioResult> {
  return api.post<ScenarioResult>('/dev/replica/scenario', { preset, gameweek })
}

// ── The answer sheet ──────────────────────────────────────────────────────────────────────────

/** One fixture's known final score, as the golden file has it. */
export interface GoldenFixture {
  id: string
  /** Canonical team ids — render them through `teamDisplayName`/`teamPickerName`. */
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
}

export interface GoldenResults {
  /** Null only when the season has no fixtures yet, in which case `fixtures` is empty. */
  gameweek: number | null
  fixtures: GoldenFixture[]
}

/**
 * The scores the bundled season is already going to end on, for one gameweek.
 *
 * Available in mock and replica, refused with a `409` in live. Fetch it once per gameweek: the
 * picker cycles through every fixture in a week and must not fire a request per card.
 */
export function getGoldenResults(gameweek?: number): Promise<GoldenResults> {
  return api.get<GoldenResults>(
    `/dev/replica/results${gameweek === undefined ? '' : `?gameweek=${gameweek}`}`
  )
}

/** The fixture-id lookup the picker wants, off the same payload the readout lists. */
export function goldenScoresByFixture(
  results: GoldenResults | null
): Record<string, { homeScore: number; awayScore: number }> {
  const map: Record<string, { homeScore: number; awayScore: number }> = {}
  for (const fixture of results?.fixtures ?? []) {
    map[fixture.id] = { homeScore: fixture.homeScore, awayScore: fixture.awayScore }
  }
  return map
}

export function injectReplicaError(
  endpoint: string,
  mode: 'auth' | 'quota' | '500' | 'timeout' | 'clear'
): Promise<{ errors: Record<string, string> }> {
  return api.post('/dev/replica/error', { endpoint, mode })
}
